import React, { useEffect } from 'react';
import { Document, Page } from 'react-pdf';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import { useDropzone } from 'react-dropzone';
import { DndContext, closestCenter, useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import { Progress } from '../../ui/progress';

const LazyPDFPage = React.memo(({ pageNumber, pdfUrl, width = 350 }) => {
  const pageRef = React.useRef(null);

  return (
    <div ref={pageRef} className="pdf-page-container">
      <Page
        pageNumber={pageNumber}
        renderTextLayer={false}
        renderAnnotationLayer={false}
        width={width}
        className="pdf-page"
        loading={
          <div className="flex items-center justify-center h-full">
            <i className="fas fa-file-alt mb-2" style={{ fontSize: '1.5rem' }}></i>
            <p>Loading page...</p>
          </div>
        }
      />
    </div>
  );
});

LazyPDFPage.displayName = 'LazyPDFPage';

// Sortable file item component
function SortableFileItem({ file, index, handleDelete, isDark }) {
  const { attributes, listeners, setNodeRef, transform } = useSortable({ id: file.name });
  const style = {
    transform: CSS.Transform.toString(transform),
  };

  return (
    <div ref={setNodeRef} {...attributes} style={style} className={`flex flex-col gap-[9px] items-start px-[10px] py-[16px] relative rounded-[6px] w-[200px] transition-colors duration-200 ${
      isDark ? 'bg-[#161515]' : 'bg-neutral-100'
    }`}>
      <div className="flex gap-[9px] items-center relative w-full">
        <div className="flex gap-[4px] items-center overflow-hidden relative cursor-move flex-1 min-w-0" {...listeners}>
          <div
            className="h-[14px] w-[13px] shrink-0 cursor-move"
            style={{
              WebkitMaskImage: 'url(/assets/icons/claimform.svg)',
              WebkitMaskSize: 'contain',
              WebkitMaskRepeat: 'no-repeat',
              WebkitMaskPosition: 'center',
              maskImage: 'url(/assets/icons/claimform.svg)',
              maskSize: 'contain',
              maskRepeat: 'no-repeat',
              maskPosition: 'center',
              backgroundColor: isDark ? '#b3b3b3' : '#072837',
              transition: 'background-color 0.2s ease'
            }}
          />
          <p className={`font-medium leading-[14px] not-italic text-[12px] m-0 truncate cursor-default transition-colors duration-200 ${
            isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'
          }`} title={file.name}>
            {file.name}
          </p>
        </div>
        <div className="content-stretch flex gap-[8px] items-center relative shrink-0">
          <a
            href={URL.createObjectURL(file)}
            target="_blank"
            rel="noopener noreferrer"
            className="overflow-clip relative shrink-0 size-[16px] cursor-pointer"
          >
            <div
              className="size-[16px] shrink-0"
              style={{
                WebkitMaskImage: 'url(/assets/icons/viewdata.svg)',
                WebkitMaskSize: 'contain',
                WebkitMaskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
                maskImage: 'url(/assets/icons/viewdata.svg)',
                maskSize: 'contain',
                maskRepeat: 'no-repeat',
                maskPosition: 'center',
                backgroundColor: '#cd6d2c',
                transition: 'background-color 0.2s ease'
              }}
            />
          </a>
          <button
            className="overflow-clip relative shrink-0 size-[16px] bg-transparent border-0 cursor-pointer p-0"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(index);
            }}
          >
            <div
              className="size-[16px] shrink-0"
              style={{
                WebkitMaskImage: 'url(/assets/icons/delete.svg)',
                WebkitMaskSize: 'contain',
                WebkitMaskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
                maskImage: 'url(/assets/icons/delete.svg)',
                maskSize: 'contain',
                maskRepeat: 'no-repeat',
                maskPosition: 'center',
                backgroundColor: isDark ? '#b3b3b3' : '#FF0000',
                transition: 'background-color 0.2s ease'
              }}
              aria-label="Delete"
            />
          </button>
        </div>
      </div>
    </div>
  );
}

const FileUploadSection = ({
  formData,
  setFormData,
  errors,
  mergedPdfUrl,
  isMerging,
  uploadProgress,
  pdfError,
  numPages,
  pageNumber,
  swiperInstance,
  setSwiperInstance,
  onDocumentLoadSuccess,
  onDocumentLoadError,
  goToNextPage,
  goToPrevPage,
  toggleColorPage,
  isDark,
  onDuplicateFile
}) => {
  const { files } = formData;

  // Inject CSS for proper PDF rendering (matching SubmittedView)
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .file-upload-pdf-preview .pdf-page canvas {
        max-width: 100% !important;
        height: auto !important;
      }
      .file-upload-pdf-preview .react-pdf__Page {
        display: flex !important;
        justify-content: center !important;
        align-items: center !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    accept: { "application/pdf": [".pdf"] },
    onDrop: async (acceptedFiles) => {
      const existingFileNames = files.map(f => f.name);
      const duplicateFile = acceptedFiles.find(f => existingFileNames.includes(f.name));

      if (duplicateFile) {
        onDuplicateFile?.(duplicateFile.name);
        return;
      }

      const updatedFiles = [...files, ...acceptedFiles];
      setFormData((prev) => ({ ...prev, files: updatedFiles }));
    },
  });

  const handleDelete = (index) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, files: updatedFiles }));
  };

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = formData.files.findIndex((file) => file.name === active.id);
      const newIndex = formData.files.findIndex((file) => file.name === over.id);
      setFormData((prev) => ({ ...prev, files: arrayMove(prev.files, oldIndex, newIndex) }));
    }
  };

  return (
    <>
    <div className={`box-border content-stretch flex flex-col gap-[24px] items-start p-[20px] relative rounded-[10px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.11)] border-none w-full max-w-[822px] transition-colors duration-200 ${
      isDark ? 'bg-[#000000]' : 'bg-white border border-[#d9d9d9]'
    }`}>
      {/* Title */}
      <p className={`font-semibold leading-[normal] relative shrink-0 text-[16px] text-nowrap whitespace-pre transition-colors duration-200 ${
        isDark ? 'text-white' : 'text-[#072837]'
      }`}>
        File Upload (Collective 20 MBs) <span className="text-[#FF0000]">*</span>
      </p>

      <div className="content-stretch flex flex-col gap-[24px] items-center justify-center relative shrink-0 w-full">
        {/* Upload Dropzone */}
        <div
          {...getRootProps()}
          className="content-stretch flex flex-col gap-[10px] items-center justify-center relative shrink-0 w-full cursor-pointer"
        >
          <input {...getInputProps()} />
          <div className="content-stretch flex flex-col gap-[16px] items-center justify-center relative shrink-0">
            {/* Upload icon */}
            <div className="overflow-clip relative shrink-0 size-[43px]">
              <div
                className="size-[43px] shrink-0"
                style={{
                  WebkitMaskImage: 'url(/assets/icons/upload.svg)',
                  WebkitMaskSize: 'contain',
                  WebkitMaskRepeat: 'no-repeat',
                  WebkitMaskPosition: 'center',
                  maskImage: 'url(/assets/icons/upload.svg)',
                  maskSize: 'contain',
                  maskRepeat: 'no-repeat',
                  maskPosition: 'center',
                  backgroundColor: isDark ? '#b3b3b3' : '#072837',
                  transition: 'background-color 0.2s ease'
                }}
                aria-label="Upload"
              />
            </div>

            {/* Upload text */}
            <div className="content-stretch flex flex-col gap-[4px] items-center justify-center relative shrink-0">
              <div className="content-stretch flex flex-col gap-[16px] items-start justify-center relative shrink-0">
                <div className="box-border content-stretch flex gap-[16px] items-center justify-center relative rounded-[12px] shadow-[0px_0px_56px_0px_rgba(0,0,0,0.04)] shrink-0">
                  <div className="content-stretch flex flex-col gap-[18px] items-start justify-center relative shrink-0">
                    <div className="content-stretch flex flex-col gap-[16px] items-start justify-center relative shrink-0">
                      <p className={`font-semibold leading-[normal] relative shrink-0 text-[16px] text-nowrap whitespace-pre m-0 transition-colors duration-200 ${
                        isDark ? 'text-white' : 'text-[#072837]'
                      }`}>
                        Upload Files
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <p className={`font-normal leading-[normal] relative shrink-0 text-[16px] text-nowrap whitespace-pre m-0 transition-colors duration-200 ${
                isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'
              }`}>
                Drag & Drop files here
              </p>
            </div>
          </div>
        </div>

        {/* Description */}
        <p className={`font-normal leading-[normal] relative shrink-0 text-[14px] text-center m-0 transition-colors duration-200 ${
          isDark ? 'text-white' : 'text-[#072837]'
        }`}>
          You can see the merged version of the uploaded files below. You can choose what page to print in color
        </p>

        {/* File List */}
        {files && files.length > 0 && (
          <div className="content-start flex flex-wrap gap-[6px] items-start relative shrink-0 w-full">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={files.map((f) => f.name)} strategy={horizontalListSortingStrategy}>
                {files.map((file, index) => (
                  <SortableFileItem key={index} file={file} index={index} handleDelete={handleDelete} isDark={isDark} />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        )}

        {/* Merging State */}
        {isMerging && (
          <div className="content-stretch flex flex-col gap-[22px] items-center relative shrink-0">
            <div className="content-stretch flex flex-col gap-[16px] items-center justify-center relative shrink-0">
              <div className="relative shrink-0 size-[52px]">
                <div
                  className="size-[52px] shrink-0"
                  style={{
                    WebkitMaskImage: 'url(/assets/icons/mergefiles.svg)',
                    WebkitMaskSize: 'contain',
                    WebkitMaskRepeat: 'no-repeat',
                    WebkitMaskPosition: 'center',
                    maskImage: 'url(/assets/icons/mergefiles.svg)',
                    maskSize: 'contain',
                    maskRepeat: 'no-repeat',
                    maskPosition: 'center',
                    backgroundColor: '#cd6d2c',
                    transition: 'background-color 0.2s ease'
                  }}
                  aria-label="Merging"
                />
              </div>
              <p className="font-semibold leading-[normal] relative shrink-0 text-[#cd6d2c] text-[14px] text-nowrap whitespace-pre transition-colors duration-200">
                Merging...
              </p>
              <p className={`font-normal leading-[normal] relative shrink-0 text-[14px] text-nowrap whitespace-pre transition-colors duration-200 ${
                isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'
              }`}>
                Please wait while the document is being merged..
              </p>
            </div>
            <Progress value={uploadProgress} className="w-full max-w-[315px]" />
          </div>
        )}

        {/* PDF Preview */}
        {mergedPdfUrl && !isMerging && (
          <div className="file-upload-pdf-preview content-stretch flex flex-col gap-[16px] items-start justify-center relative shrink-0 w-full">
            <div className={`box-border content-stretch flex flex-col gap-[16px] items-center px-[20px] py-[16px] relative rounded-[10px] shrink-0 w-full transition-colors duration-200 ${
              isDark ? 'bg-[#161515] [border:1px_solid_#292929]' : 'bg-[#fff5eb] [border:1px_solid_#e9e9e7]'
            }`}>
              {/* Header */}
              <div className="content-stretch flex flex-col gap-[6px] items-center justify-center relative shrink-0 w-full">
                <p className={`font-semibold leading-[normal] relative shrink-0 text-[16px] m-0 transition-colors duration-200 ${
                  isDark ? 'text-white' : 'text-[#072837]'
                }`}>
                  Merged View to Color Pages
                </p>
                <p className={`font-normal leading-[normal] relative shrink-0 text-[14px] text-center m-0 transition-colors duration-200 ${
                  isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'
                }`}>
                  Please confirm which pages you need in colour (outside of first page) - please only if absolutely required.
                </p>
              </div>

              {/* PDF Viewer */}
              <Swiper
                navigation={false}
                pagination={false}
                modules={[Navigation, Pagination]}
                className="w-full"
                onSwiper={setSwiperInstance}
                allowTouchMove={false}
              >
                <SwiperSlide>
                  <div className="content-stretch flex flex-col gap-[16px] items-center relative shrink-0 w-full">
                    {/* PDF Container */}
                    <div className={`border-solid border-[1px] min-h-[600px] relative rounded-[8px] shrink-0 w-full overflow-auto flex items-center justify-center p-4 transition-colors duration-200 ${
                      isDark ? 'bg-[#000000] border-[#292929]' : 'bg-white border-[#cbcbcb]'
                    }`}>
                      {pdfError ? (
                        <div className="text-center p-4 transition-colors duration-200 text-[#ff4d4f]">
                          Failed to load PDF preview
                        </div>
                      ) : (
                        <Document
                          file={mergedPdfUrl}
                          onLoadSuccess={onDocumentLoadSuccess}
                          onLoadError={onDocumentLoadError}
                          loading={
                            <div className={`text-center p-4 transition-colors duration-200 ${
                              isDark ? 'text-white' : 'text-[#333333]'
                            }`}>
                              Loading preview...
                            </div>
                          }
                        >
                          <Page
                            pageNumber={pageNumber}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                            scale={1.5}
                            className="pdf-page"
                          />
                        </Document>
                      )}
                    </div>

                    {/* Navigation Controls */}
                    {!pdfError && (
                      <div className="content-stretch flex gap-[16px] items-center relative shrink-0 w-full">
                        <button
                          onClick={goToPrevPage}
                          disabled={pageNumber <= 1}
                          className={`flex items-center justify-center bg-transparent border-0 cursor-pointer p-0 ${pageNumber <= 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <ChevronLeft className={`w-[18px] h-[18px] transition-colors duration-200 ${
                            isDark ? 'text-white' : 'text-[#072837]'
                          }`} />
                        </button>

                        <div className="flex-1 flex flex-col items-center justify-center">
                          <p className={`font-semibold text-[14px] m-0 transition-colors duration-200 ${
                            isDark ? 'text-[#b0b0b0]' : 'text-[#898989]'
                          }`}>
                            {pageNumber} of {numPages}
                          </p>
                        </div>

                        <button
                          onClick={goToNextPage}
                          disabled={pageNumber >= numPages}
                          className={`flex items-center justify-center bg-transparent border-0 cursor-pointer p-0 ${pageNumber >= numPages ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <ChevronRight className={`w-[18px] h-[18px] transition-colors duration-200 ${
                            isDark ? 'text-white' : 'text-[#072837]'
                          }`} />
                        </button>
                      </div>
                    )}
                  </div>
                </SwiperSlide>
              </Swiper>

              {/* Mark Colored Button */}
              <button
                onClick={toggleColorPage}
                className={`border-solid border-[1px] content-stretch flex gap-[10px] h-[31px] items-center justify-center px-[16px] py-[10px] relative rounded-[7px] shrink-0 w-full cursor-pointer transition-colors duration-200 ${
                  formData.colorPages === 'true'
                    ? 'bg-[#cd6d2c] border-[#cd6d2c]'
                    : isDark
                      ? 'bg-[#161515] border-[#292929] hover:bg-[#222222]'
                      : 'bg-white border-[#d9d9d9] hover:bg-gray-50'
                }`}
              >
                <div className="overflow-clip relative shrink-0 size-[16px]">
                  <div
                    className="size-[16px] shrink-0"
                    style={{
                      WebkitMaskImage: 'url(/assets/icons/markcolored.svg)',
                      WebkitMaskSize: 'contain',
                      WebkitMaskRepeat: 'no-repeat',
                      WebkitMaskPosition: 'center',
                      maskImage: 'url(/assets/icons/markcolored.svg)',
                      maskSize: 'contain',
                      maskRepeat: 'no-repeat',
                      maskPosition: 'center',
                      backgroundColor: formData.colorPages === 'true'
                        ? (isDark ? '#000000' : '#ffffff')
                        : '#cd6d2c',
                      transition: 'background-color 0.2s ease'
                    }}
                    aria-label="Mark Colored"
                  />
                </div>
                <p className={`font-medium leading-[normal] relative shrink-0 text-[12px] text-nowrap whitespace-pre m-0 transition-colors duration-200 ${
                  formData.colorPages === 'true'
                    ? isDark ? 'text-[#000000]' : 'text-white'
                    : 'text-[#cd6d2c]'
                }`}>
                  {formData.colorPages === 'true' ? 'Marked Colored' : 'Mark all pages colored'}
                </p>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    {errors.fileUpload && (
      <div className="w-full max-w-[822px] rounded-[16px] px-[8px] flex items-start gap-[8px] text-[14px] font-semibold text-[#ff0000]">
        <AlertCircle className="h-[16px] w-[16px] flex-shrink-0" />
        <span className="leading-[1.4]">Files are required. You can only upload files with maximum of collective 20 MBs.</span>
      </div>
    )}
    {errors.mergedPDF && (
      <div className="w-full max-w-[822px] rounded-[16px] px-[8px] flex items-start gap-[8px] text-[14px] font-semibold text-[#ff0000]">
        <AlertCircle className="h-[16px] w-[16px] flex-shrink-0" />
        <span className="leading-[1.4]">Please wait a few seconds for the PDF to merge.</span>
      </div>
    )}
    </>
  );
};

export default FileUploadSection;